import {
  all,
  call,
  delay,
  getContext,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
  throttle,
} from 'redux-saga/effects';
import { damzOnline } from '../actions/initialization';
import { SupabaseClient } from '@supabase/supabase-js';
import { Database } from '@wix/da-papi-types/lib/supabase';
import { getCurrentUser } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { PapiUser } from '@wix/da-papi-types';
import {
  decrementGlobalCounter,
  delayedRefreshCounter,
  incrementGlobalCounter,
  refreshCounter,
  setGlobalCounter,
} from '../actions/counter';
import ddt from '@wix/da-ddt';
import { Url } from '@wix/da-url';
import { getCounterConversations } from '../selectors/global';

let originalFaviconUrl: string | null = null;

function setFavicon(counter: number) {
  const faviconNode = document.querySelector('link[rel="shortcut icon"]');
  if (faviconNode) {
    if (!originalFaviconUrl) {
      originalFaviconUrl = faviconNode.getAttribute('href');
    }
    if (counter > 0) {
      faviconNode.setAttribute(
        'href',
        Url.staticCDNLink('eclipse/icons/favicon-dot_32x32.png')
      );
    } else {
      faviconNode.setAttribute('href', originalFaviconUrl ?? '');
    }
  }
}

function* fetchFreshCounts() {
  const user: PapiUser = yield select(getCurrentUser);
  const client: SupabaseClient<Database, 'damz'> =
    yield getContext('supaClient');

  const { data, error } = yield client
    .from('user_unread_count')
    .select('conversations, requests')
    .eq('user_id', user.useridUuid)
    .maybeSingle();
  if (error) {
    ddt.error('damz', 'Failed to get global counter', error);
    return;
  }

  if (data) {
    // Sets the badge value on the PWA icon
    if ('setAppBadge' in navigator) {
      try {
        navigator.setAppBadge(data.conversations ?? 0);
      } catch (e) {}
    }
    setFavicon(data.conversations);
    yield put(setGlobalCounter(data.conversations, data.requests));
  }
}

function* handleGlobalCounterChange(
  action: ReturnType<typeof decrementGlobalCounter>
) {
  const counter: ReturnType<typeof getCounterConversations> = yield select(
    getCounterConversations
  );
  setFavicon(counter);
}

function* handleDelayedRefreshCounter(
  action: ReturnType<typeof delayedRefreshCounter>
) {
  yield delay(action.payload.delay);
  yield put(refreshCounter());
}

/**
 * Handles counts
 */
export function* counterSaga() {
  yield take(damzOnline); // wait for online to happen
  yield all([
    call(fetchFreshCounts), // call on pageload
    throttle(1000, refreshCounter, fetchFreshCounts), // throttle on refresh
    takeLatest(delayedRefreshCounter, handleDelayedRefreshCounter),
    takeEvery(
      [decrementGlobalCounter, incrementGlobalCounter],
      handleGlobalCounterChange
    ),
  ]);
}
